import { common } from '../common.js';
import type { IProgram } from '../types.js';


/** Post-graduation work permit. */
export const PGWP: IProgram = {
  id: 'pgwp',
  name: 'Post-graduation work permit (PGWP)',
  short: 'PGWP',
  family: 'temporary',
  status: 'active',
  maintainedStatus: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [
    { title: 'IRCC: Post-graduation work permit, how to apply', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/study-canada/work/after-graduation/apply.html', retrieved: '2026-10-04' },
    { title: 'IRCC: Post-graduation work permit, who can apply', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/study-canada/work/after-graduation/eligibility.html', retrieved: '2026-10-04' }
  ],
  questions: [
    { id: 'passportExtension', text: 'Is this only to extend a PGWP because the passport expired?', kind: 'yesno' }
  ],
  roles: [{ id: 'principal', label: 'Graduate', min: 1, max: 1 }],
  sections: ['Forms', 'Studies', 'Identity and civil documents', 'Fees'],
  items: [
    { id: 'personalChecklist', label: 'IRCC personalized document checklist (from the IRCC portal or APR portal)', section: 'Forms', forRoles: 'application', source: 0 },
    common.representative(0),
    { id: 'completion', label: 'Proof of completing the program (completion letter)', section: 'Studies', forRoles: ['principal'], source: 1 },
    { id: 'transcript', label: 'Final transcript', section: 'Studies', forRoles: ['principal'], source: 1 },
    { id: 'language', label: 'Language test results', section: 'Studies', forRoles: ['principal'], when: { not: { q: 'passportExtension', is: true } }, source: 0 },
    common.passport(0, ['principal']),
    { id: 'studyPermit', label: 'Study permit (current or last)', section: 'Identity and civil documents', forRoles: ['principal'], source: 1 },
    common.fees(0)
  ],
  deadlines: [
    {
      id: 'pgwpApply', event: 'programCompleted', label: 'Apply for the post-graduation work permit', days: 180,
      citation: 'IRCC: apply within 180 days after getting confirmation that the program is completed (final marks, a transcript or a letter from the school)',
      caveat: 'Counted from the first confirmation the school gave. If the study permit expires before then, status must also be restored (within 90 days).',
      review: 'pending'
    }
  ]
};

/** Employer-specific work permit with an LMIA. */
export const WORK_PERMIT_LMIA: IProgram = {
  id: 'work-permit-lmia',
  name: 'Employer-specific work permit (with an LMIA)',
  short: 'Work permit (LMIA)',
  family: 'temporary',
  status: 'active',
  maintainedStatus: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [
    { title: 'IRCC: Work permit, how to apply', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/work-canada/permit/temporary/apply.html', retrieved: '2026-10-04' }
  ],
  questions: [
    { id: 'inCanada', text: 'Is the applicant applying from inside Canada?', kind: 'yesno' },
    { id: 'family', text: 'Is family coming with them?', kind: 'yesno' }
  ],
  roles: [{ id: 'principal', label: 'Worker', min: 1, max: 1 }, { id: 'dependant', label: 'Family member', min: 0, max: 10 }],
  sections: ['Forms', 'Job', 'Identity and civil documents', 'Photos and police certificates', 'Fees'],
  items: [
    { id: 'personalChecklist', label: 'IRCC personalized document checklist (from the IRCC portal or APR portal)', section: 'Forms', forRoles: 'application', source: 0 },
    common.representative(0),
    { id: 'lmia', label: 'Copy of the positive LMIA decision letter (and its number)', section: 'Job', forRoles: ['principal'], source: 0 },
    { id: 'jobOffer', label: 'Job offer or employment contract', section: 'Job', forRoles: ['principal'], source: 0 },
    { id: 'qualifications', label: 'Proof of the qualifications and experience the job needs', section: 'Job', forRoles: ['principal'], source: 0 },
    common.passport(0, ['principal', 'dependant']),
    { id: 'status', label: 'Current status in Canada', section: 'Identity and civil documents', forRoles: ['principal'], when: { q: 'inCanada', is: true }, source: 0 },
    { id: 'relationship', label: 'Proof of the relationship to family members coming', section: 'Identity and civil documents', forRoles: 'application', when: { q: 'family', is: true }, source: 0, translationIfNotEnFr: true },
    common.photos(0, ['principal', 'dependant']),
    common.translations(0),
    common.fees(0)
  ],
  deadlines: []
};

/** Employer-specific work permit without an LMIA (offer submitted through the IRCC Employer Portal). */
export const WORK_PERMIT_LMIA_EXEMPT: IProgram = {
  ...WORK_PERMIT_LMIA,
  id: 'work-permit-lmia-exempt',
  name: 'Employer-specific work permit (LMIA-exempt, Employer Portal)',
  short: 'Work permit (LMIA-exempt)',
  items: WORK_PERMIT_LMIA.items.map(i => (i.id === 'lmia'
    ? { ...i, id: 'offerNumber', label: 'Offer of employment number from the IRCC Employer Portal (and the employer compliance fee paid)' }
    : i))
};

/** International Experience Canada (working holiday, young professionals, international co-op). */
export const IEC: IProgram = {
  id: 'iec',
  name: 'International Experience Canada (IEC)',
  short: 'IEC',
  family: 'temporary',
  status: 'active',
  maintainedStatus: true,
  version: '2026-10-04',
  review: 'pending',
  sources: [
    { title: 'IRCC: International Experience Canada', url: 'https://www.canada.ca/en/immigration-refugees-citizenship/services/work-canada/iec.html', retrieved: '2026-10-04' }
  ],
  questions: [
    { id: 'category', text: 'Which category?', kind: 'choice', choices: ['Working Holiday', 'Young Professionals', 'International Co-op (Internship)'] }
  ],
  roles: [{ id: 'principal', label: 'Participant', min: 1, max: 1 }],
  sections: ['Forms', 'Job', 'Identity and civil documents', 'Photos and police certificates', 'Fees'],
  items: [
    { id: 'invitation', label: 'Invitation to apply (from the IEC pool)', section: 'Forms', forRoles: 'application', source: 0 },
    { id: 'personalChecklist', label: 'IRCC personalized document checklist', section: 'Forms', forRoles: 'application', source: 0 },
    common.representative(0),
    { id: 'offer', label: 'Offer of employment number from the employer (Young Professionals and Co-op)', section: 'Job', forRoles: ['principal'], when: { not: { q: 'category', is: 'Working Holiday' } }, source: 0 },
    common.passport(0, ['principal']),
    { id: 'police', label: 'Police certificates', section: 'Photos and police certificates', forRoles: ['principal'], source: 0, translationIfNotEnFr: true },
    { id: 'insurance', label: 'Health insurance for the whole stay (shown at the border)', section: 'Identity and civil documents', forRoles: ['principal'], source: 0 },
    common.fees(0)
  ],
  deadlines: []
};
